import { AuxIcon } from "@blk/aladdin-react-components-es";
import { AuxIconStateEnum } from "@blk/aladdin-web-components";
import React from "react";
import { batch, useDispatch, useSelector } from "react-redux";
import { useConditionalVisibility } from "../hooks";
import { InsEditJsonWidgetIcon } from "./insEditJsonWidgetIcon";
import { getByValue } from "common/array-helper";
import { preview } from "common/constants";
import { ModalName } from "components/modal/types";
import { widgetNameToWidgetType } from "components/modal/widget-workshop/types";
import { getDatapointGridStateId } from "components/widgets/grid/common";
import { useWidgetComponentProps } from "components/widgets/hooks/use-current-widget";
import { useCurrentDashboardType, useDashboardFullPathInformations } from "components/workspaces/hooks/use-current-dashboard";
import { datasetNameToDatapointIdMap } from "datapoints";
import { language } from "language/default";
import { widgetSelector } from "store/cross-slice/widgetSelector";
import { saveState } from "store/grids";
import { openModal } from "store/modal";
import { pushIfNeeded, removeWidgetFromSection, selectedSectionIndexSelector } from "store/template";
import { setWorkshopComponentProps, setWorkshopHeader } from "store/widget-workshop";
const extractColumnsWithGrouping = (columnsTemplate) => columnsTemplate.map((col) => ({
    colId: col.colId || col.field,
    rowGroup: Boolean(col.rowGroup),
    rowGroupIndex: col.rowGroup && typeof col.rowGroupIndex === "number" && col.rowGroupIndex >= 0 ? col.rowGroupIndex : undefined
})).filter((col) => col.rowGroup);
const mergeWidgetGridStateWithGrouping = (widgetGridState, columnsWithGrouping) => {
    const mergedColumns = widgetGridState.columns.map((c) => {
        const groupingCol = columnsWithGrouping.find((gc) => gc.colId === c.colId);
        if (groupingCol) {
            return { ...c, rowGroup: groupingCol.rowGroup, rowGroupIndex: groupingCol.rowGroupIndex };
        }
        return { ...c };
    });
    columnsWithGrouping.forEach((gc) => {
        if (!mergedColumns.some((c) => c.colId === gc.colId)) {
            mergedColumns.push(gc);
        }
    });
    return mergedColumns;
};
const getDatasetFromComponentProps = (originalCompProps) => {
    const dimensions = originalCompProps.datapointParams?.[0]?.dimensions;
    const standardDatapoint = dimensions?.length
        ? dimensions[dimensions.length - 1].standardDatapoint
        : "";
    return getByValue(datasetNameToDatapointIdMap, standardDatapoint);
};
export const InsWidgetEditModeControls = (props) => {
    const { widgetId, sectionId, groupId } = props, entityType = useCurrentDashboardType(), originalCompProps = useWidgetComponentProps(sectionId, widgetId), widgetMetaData = useSelector((state) => {
        const widget = widgetSelector(state, widgetId || "");
        return widget?.metaData ?? widget?.metadata;
    }), widgetName = useSelector((state) => widgetSelector(state, widgetId || "")?.name), widgetType = useSelector((state) => {
        const widget = widgetSelector(state, widgetId || "");
        return widget?.componentProps?.chartType ?? widget?.componentName;
    });
    const dispatch = useDispatch();
    const conditionalVisibility = useConditionalVisibility(), { pathInformations, selectedSection } = useDashboardFullPathInformations();
    const selectedSectionIndex = useSelector((state) => selectedSectionIndexSelector(state, selectedSection));
    const widgetStateId = getDatapointGridStateId(sectionId || "", widgetId || "");
    const widgetGridState = useSelector((state) => state.grids.state[widgetStateId]);
    const removeWidget = (e) => {
        e.stopPropagation();
        if (widgetId && sectionId) {
            batch(() => {
                dispatch(removeWidgetFromSection({ conditionalVisibility, index: 0, sectionId, widgetId: groupId || widgetId }));
                pushIfNeeded(dispatch, selectedSectionIndex, selectedSection, pathInformations);
            });
        }
    };
    const editWidget = (e) => {
        e.stopPropagation();
        if (!widgetId || !sectionId) {
            return;
        }
        batch(() => {
            const previewStateId = getDatapointGridStateId(preview.sectionId, preview.widgetId);
            const columnsTemplate = originalCompProps?.columnsTemplate?.columns || [];
            const columnsWithGrouping = extractColumnsWithGrouping(columnsTemplate);
            let columnsToSave = [];
            if (widgetGridState?.columns) {
                columnsToSave = mergeWidgetGridStateWithGrouping(widgetGridState, columnsWithGrouping);
            }
            else if (columnsWithGrouping.length > 0) {
                columnsToSave = columnsWithGrouping;
            }
            dispatch(saveState({ columns: columnsToSave, id: previewStateId }));
            dispatch(openModal({
                content: { id: ModalName[ModalName.WidgetWorkshop], modalName: ModalName.WidgetWorkshop },
                title: language.en.dashboard.edit.editWidget,
                type: "action"
            }));
            dispatch(setWorkshopHeader({
                dataset: getDatasetFromComponentProps(originalCompProps),
                entityType: entityType,
                id: widgetId,
                metaData: { ...widgetMetaData, entityType: [entityType] },
                name: widgetName,
                widgetType: widgetType ? widgetNameToWidgetType.get(widgetType) : undefined
            }));
            dispatch(setWorkshopComponentProps(originalCompProps));
        });
    };
    return React.createElement(React.Fragment, null,
        React.createElement(InsEditJsonWidgetIcon, { sectionId: sectionId || "", widgetId: widgetId || "" }),
        React.createElement(AuxIcon, { slot: "target", className: "widgetDeleteIcon", type: "widget-delete", state: AuxIconStateEnum["secondary-button"], title: "Delete widget", onMouseDown: removeWidget }),
        widgetMetaData?.editableWidget
            ? React.createElement(React.Fragment, null,
                React.createElement(AuxIcon, { slot: "target", className: "widgetDeleteIcon", type: "edit", state: AuxIconStateEnum["secondary-button"], title: "Edit widget", onMouseDown: editWidget, onMouseUp: editWidget }))
            : React.createElement(React.Fragment, null));
};
